/**
 * League (designs/Caged League, docs/game-design.md §7, §12): a series of 8 fighters. The
 * player's own shows today's fight and the game plan; any other series can be browsed. The table
 * (W, D, L, KO wins, damage +/−, points) marks the promotion, qualifier and relegation zones,
 * and the fixtures list every day's results.
 */
import { LEAGUES, type FixtureView, type SeriesView, type TableRowView, type Zone } from '@caged/shared';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { countryName } from '../../hud/format';
import { useLeague } from '../../state/league';
import { useApp } from '../../state/store';
import { ArenaBackdrop } from '../components/Backdrop';
import { DesktopHeader, MenuButton } from '../components/Chrome';
import { FighterArt } from '../components/FighterArt';
import { useDuration } from '../components/ProgressWidgets';
import { ReminderPrompt } from '../components/Reminders';
import ui from '../components/ui.module.css';
import { useLayoutMode, useMyLook, useNow, useScrollTop } from '../hooks';
import { fightPhase, FightChip, Flag, signedHealth, todayFixture, useClockTime, useResultLabel, useSeriesTitle } from '../league';
import '../ui.css';
import s from './League.module.css';

const ZONE_CLASS: Record<Zone, string> = {
  champion: s.zoneUp!,
  promotion: s.zoneUp!,
  promotionQualifier: s.zoneQual!,
  stay: '',
  relegationQualifier: s.zoneRisk!,
  relegated: s.zoneDown!,
};

/** Every fighter in a series fights for its country, so each player's row carries the country's flag (bots none, D44). */
function Table({ rows, country }: { rows: readonly TableRowView[]; country: string }) {
  const { t } = useTranslation();
  return (
    <section className={[ui.card, s.tableCard].join(' ')} aria-label={t('screens.league.standings')}>
      <div className={[s.tableRow, s.tableHead].join(' ')} aria-hidden="true">
        <span>{t('screens.league.table.pos')}</span>
        <span>{t('screens.league.table.fighter')}</span>
        <span>{t('screens.league.table.won')}</span>
        <span>{t('screens.league.table.drawn')}</span>
        <span>{t('screens.league.table.lost')}</span>
        <span className={s.wideOnly}>{t('screens.league.table.ko')}</span>
        <span className={s.wideOnly}>{t('screens.league.table.damage')}</span>
        <span>{t('screens.league.table.points')}</span>
      </div>
      <ol className={s.tableBody}>
        {rows.map((r, i) => {
          const next = rows[i + 1];
          const edge = next && next.zone !== r.zone;
          return (
            <li key={r.slot} className={[s.tableRow, ZONE_CLASS[r.zone], r.me ? s.meRow : '', edge ? s.zoneEdge : ''].join(' ')}>
              <span className={s.pos}>{String(r.position).padStart(2, '0')}</span>
              <span className={s.who}>
                <Flag country={country} decorative blank={r.bot} />
                <span className={s.whoName} data-name="">
                  {r.name}
                </span>
                {r.me && <span className={s.tagYou}>{t('screens.league.you')}</span>}
                {r.bot && <span className={s.tagBot}>{t('screens.league.bot')}</span>}
              </span>
              <span className={s.num}>{r.won}</span>
              <span className={s.num}>{r.drawn}</span>
              <span className={s.num}>{r.lost}</span>
              <span className={[s.num, s.wideOnly].join(' ')}>{r.koWins}</span>
              <span className={[s.num, s.wideOnly].join(' ')}>{signedHealth(r.damageFor - r.damageAgainst)}</span>
              <span className={s.pts}>{r.points}</span>
            </li>
          );
        })}
      </ol>
      <Legend rows={rows} />
    </section>
  );
}

/** Which zones this table has, explained once under it. */
function Legend({ rows }: { rows: readonly TableRowView[] }) {
  const { t } = useTranslation();
  const zones = [...new Set(rows.map((r) => r.zone))].filter((z) => z !== 'stay');
  if (!zones.length) return null;
  return (
    <ul className={s.legend}>
      {zones.map((z) => (
        <li key={z}>
          <span className={[s.legendDot, ZONE_CLASS[z]].join(' ')} aria-hidden="true" />
          {t(`screens.league.zones.${z}`)}
        </li>
      ))}
    </ul>
  );
}

function Fixtures({ fixtures, mySlot, day: initial, country }: { fixtures: readonly FixtureView[]; mySlot: number | null; day: number; country: string }) {
  const { t } = useTranslation();
  const result = useResultLabel();
  const watchReplay = useApp((st) => st.watchReplay);
  const days = useMemo(() => [...new Set(fixtures.map((f) => f.day))].sort((a, b) => a - b), [fixtures]);
  const [day, setDay] = useState(() => (days.includes(initial) ? initial : (days[days.length - 1] ?? 1)));
  const list = fixtures.filter((f) => f.day === day);
  const at = days.indexOf(day);
  const name = (f: FixtureView, side: 0 | 1) => {
    const x = side === 0 ? f.a : f.b;
    return x.series ? `${x.name} · ${x.series}` : x.name;
  };
  return (
    <section className={[ui.card, s.fixtures].join(' ')} aria-label={t('screens.league.fixtures')}>
      <div className={s.fixturesHead}>
        <button type="button" className={s.dayBtn} onClick={() => setDay(days[at - 1]!)} disabled={at <= 0} aria-label={t('screens.league.prevDay')}>
          ‹
        </button>
        <span className={ui.label}>{day === LEAGUES.qualifierDay ? t('screens.league.qualifiers') : t('screens.league.dayN', { day })}</span>
        <button type="button" className={s.dayBtn} onClick={() => setDay(days[at + 1]!)} disabled={at >= days.length - 1} aria-label={t('screens.league.nextDay')}>
          ›
        </button>
      </div>
      {list.map((f) => {
        const mine = mySlot !== null && ((f.a.slot === mySlot && !f.a.series) || (f.b.slot === mySlot && !f.b.series));
        const cells = (
          <>
            <span className={[s.fixA, f.winner === 0 ? s.winner : ''].join(' ')} data-name="">
              {name(f, 0)}
            </span>
            <span className={s.fixCenter}>
              <Flag country={country} decorative blank={f.a.bot} />
              <span className={[s.fixMid, f.state === 'done' ? s.fixDone : f.state === 'live' ? s.fixLive : ''].join(' ')}>
                {result(f)}
                {f.fightId && <span className={s.play} aria-hidden="true"> ▷</span>}
              </span>
              <Flag country={country} decorative blank={f.b.bot} />
            </span>
            <span className={[s.fixB, f.winner === 1 ? s.winner : ''].join(' ')} data-name="">
              {name(f, 1)}
            </span>
          </>
        );
        // A fought fixture can be watched again (§7.4).
        return f.state === 'done' && f.fightId ? (
          <button
            key={f.id}
            type="button"
            className={[s.fixture, s.fixtureBtn, mine ? s.fixtureMine : ''].join(' ')}
            onClick={() => void watchReplay(f.fightId!, f.day)}
            aria-label={t('screens.league.watch', { a: f.a.name, b: f.b.name })}
          >
            {cells}
          </button>
        ) : (
          <div key={f.id} className={[s.fixture, mine ? s.fixtureMine : ''].join(' ')}>
            {cells}
          </div>
        );
      })}
    </section>
  );
}

/** The player's fight of the day: the time and the opponent, or what happened to it. */
function FightCard({ view, mySlot, joinedDay, now, waiting }: { view: SeriesView; mySlot: number; joinedDay: number | null; now: number; waiting: boolean }) {
  const { t } = useTranslation();
  const openLeague = useApp((st) => st.openLeague);
  const startLeagueFight = useApp((st) => st.startLeagueFight);
  const watchReplay = useApp((st) => st.watchReplay);
  const duration = useDuration();
  const time = useClockTime();
  const result = useResultLabel();
  const myLook = useMyLook();
  const { next } = view;
  const phase = fightPhase(next, now);
  const { fixture, later, meSide, opp, me, oppRow } = todayFixture(view, mySlot, joinedDay);
  const over = view.daysDone >= LEAGUES.seasonDays;

  let status: ReactNode;
  if (over) status = <p className={s.fightNote}>{t('screens.league.seasonOver')}</p>;
  else if (later) status = <p className={s.fightNote}>{t('screens.league.joinedLate')}</p>;
  else if (!fixture) status = <p className={s.fightNote}>{t('screens.league.restDay')}</p>;
  else if (fixture.state === 'done') {
    const outcome = fixture.winner === null ? 'draw' : fixture.winner === meSide ? 'won' : 'lost';
    status = (
      <>
        <p className={[s.fightNote, outcome === 'won' ? s.green : outcome === 'lost' ? s.red : ''].join(' ')}>
          {t(`screens.league.outcome.${outcome}`)} · {result(fixture)}
        </p>
        {fixture.fightId && (
          <button type="button" className={[ui.secondary, s.watchBtn].join(' ')} onClick={() => void watchReplay(fixture.fightId!, fixture.day)}>
            {t('screens.league.watchReplay')}
            <span aria-hidden="true">▷</span>
          </button>
        )}
      </>
    );
  } else if (phase === 'live' && (fixture.state === 'scheduled' || waiting)) {
    // The live window (L3): fight now, live if the opponent comes, else against their game plan.
    status = (
      <>
        <p className={[s.fightNote, s.amber].join(' ')}>
          {waiting ? t('screens.league.waitingForYou', { name: opp?.name ?? '' }) : t('screens.league.liveNow', { time: duration(next.windowEndsAt - now) })}
        </p>
        <button type="button" className={[ui.primary, s.fightNow].join(' ')} onClick={() => startLeagueFight(fixture.id)}>
          {t('screens.league.fightNow')}
          <span className={ui.arrow} aria-hidden="true">
            ▷
          </span>
        </button>
      </>
    );
  } else if (phase === 'live') status = <p className={[s.fightNote, s.amber].join(' ')}>{t('screens.league.fightingNow')}</p>;
  else if (phase === 'closing') status = <p className={s.fightNote}>{t('screens.league.resultsSoon')}</p>;
  // Reminders: the ways to get one, or when it comes (D56).
  else if (phase === 'upcoming') status = <ReminderPrompt fightAt={next.fightAt} />;

  return (
    <section className={s.fightCard} aria-label={t('screens.league.todayFight')}>
      <div className={s.fightTop}>
        <div className={s.fightTime}>
          <span className={ui.label}>{next.day === LEAGUES.qualifierDay ? t('screens.league.qualifierFight') : t('screens.league.dayN', { day: next.day })}</span>
          <span className={s.bigTime}>{time(next.fightAt)}</span>
        </div>
        {phase === 'upcoming' && !over && (
          <div className={s.fightIn}>
            <span className={ui.label}>{t('screens.league.startsIn')}</span>
            <span className={s.countdown}>{duration(next.fightAt - now)}</span>
          </div>
        )}
      </div>
      {fixture && opp && me && (
        <div className={s.versus}>
          <div className={s.side}>
            <FighterArt id={me.fighterId} height={88} look={myLook} />
            <span className={s.sideNameRow}>
              <Flag country={view.series.country} decorative />
              <span className={s.sideName} data-name="">
                {me.name}
              </span>
            </span>
            <span className={[s.sideMeta, s.amber].join(' ')}>{t('screens.league.posPts', { pos: me.position, pts: me.points })}</span>
          </div>
          <span className={s.vs}>{t('screens.league.vs')}</span>
          <div className={s.side}>
            <FighterArt id={opp.fighterId} height={88} flip delay={0.4} />
            <span className={s.sideNameRow}>
              <span className={s.sideName} data-name="">
                {opp.name}
              </span>
              {!opp.bot && <Flag country={view.series.country} decorative />}
            </span>
            <span className={[s.sideMeta, s.green].join(' ')}>
              {oppRow ? t('screens.league.posPts', { pos: oppRow.position, pts: oppRow.points }) : (opp.series ?? '')}
            </span>
          </div>
        </div>
      )}
      {status}
      <button type="button" className={[ui.primary, s.planBtn].join(' ')} onClick={() => openLeague('plan')}>
        <span className={s.planText}>
          <span>{t('screens.league.setPlan')}</span>
          <span className={s.planSub}>{t('screens.league.setPlanSub')}</span>
        </span>
        <span className={ui.arrow} aria-hidden="true">
          →
        </span>
      </button>
    </section>
  );
}

function Title({ view, country }: { view: SeriesView; country: string }) {
  const { t, i18n } = useTranslation();
  const title = useSeriesTitle();
  const countryLabel = useMemo(() => {
    try {
      return new Intl.DisplayNames([i18n.language, 'en'], { type: 'region' }).of(country) ?? country;
    } catch {
      return country;
    }
  }, [country, i18n.language]);
  const day = Math.min(LEAGUES.seasonDays, view.daysDone + 1);
  return (
    <div className={s.title}>
      <span className={s.kicker}>
        <Flag country={country} /> {countryLabel} · {t('screens.league.season', { season: view.season })}
      </span>
      <h1 className={s.titleText}>{title(view.series)}</h1>
      <span className={s.sub}>{t('screens.league.dayOf', { day, days: LEAGUES.seasonDays })}</span>
    </div>
  );
}

function Page({
  view,
  country,
  mySlot,
  joinedDay,
  waiting = false,
  movingTo = null,
}: {
  view: SeriesView;
  country: string;
  mySlot: number | null;
  joinedDay: number | null;
  waiting?: boolean;
  /** The player moves to this country's league when the season ends (L8). */
  movingTo?: string | null;
}) {
  const { t, i18n } = useTranslation();
  const layout = useLayoutMode();
  const leagueBack = useApp((st) => st.leagueBack);
  const openLeague = useApp((st) => st.openLeague);
  const now = useNow(15_000);
  const day = Math.min(LEAGUES.seasonDays, view.daysDone + (view.daysDone >= LEAGUES.roundRobinDays ? 1 : 0)) || 1;
  const card = mySlot !== null ? <FightCard view={view} mySlot={mySlot} joinedDay={joinedDay} now={now} waiting={waiting} /> : null;
  const notes =
    mySlot !== null ? (
      <>
        {joinedDay !== null && joinedDay > 1 && <p className={s.note}>{t('screens.league.inherited', { day: joinedDay })}</p>}
        {movingTo && <p className={s.note}>{t('screens.league.movingNote', { country: countryName(movingTo, i18n.language) })}</p>}
      </>
    ) : null;
  const others = (compact: boolean) => (
    <button type="button" className={s.countryBtn} onClick={() => openLeague('leagues')} aria-label={compact ? t('screens.league.otherLeagues') : undefined}>
      <Flag country={country} />
      {compact ? country : t('screens.league.otherLeagues')}
      <span aria-hidden="true">⌄</span>
    </button>
  );

  if (layout === 'desktop') {
    return (
      <div className={['cg-ui', ui.screen].join(' ')}>
        <DesktopHeader />
        <div className={s.deskHero}>
          <ArenaBackdrop tone="menu" shade="linear-gradient(90deg,rgba(20,19,18,.92),rgba(20,19,18,.5) 60%,rgba(20,19,18,.85))" />
          <div className={s.deskHeroInner}>
            <div className={s.deskHeroTop}>
              <button type="button" className={s.backLink} onClick={leagueBack}>
                ← {t('common.back')}
              </button>
              <FightChip next={view.next} now={now} />
            </div>
            <div className={s.deskHeroRow}>
              <Title view={view} country={country} />
              {others(false)}
            </div>
          </div>
        </div>
        <main className={s.deskMain}>
          <section className={s.deskTable}>
            <Table rows={view.table} country={view.series.country} />
            {notes}
          </section>
          <aside className={s.deskAside}>
            {card}
            <Fixtures fixtures={view.fixtures} mySlot={mySlot} day={day} country={view.series.country} />
          </aside>
        </main>
      </div>
    );
  }

  return (
    <div className={['cg-ui', ui.screen].join(' ')}>
      <div className={s.phoneBg} aria-hidden="true">
        <ArenaBackdrop tone="menu" shade="linear-gradient(180deg,rgba(20,19,18,.8),rgba(20,19,18,.3) 45%,#141312)" />
      </div>
      <div className={ui.column}>
        <div className={s.topBar}>
          <button type="button" className={ui.iconBtn} onClick={leagueBack} aria-label={t('common.back')}>
            <span aria-hidden="true">←</span>
          </button>
          {/* Phones: the countdown only (the fight card shows the time), and the flag. */}
          <FightChip next={view.next} now={now} compact />
          <div className={s.topEnd}>
            {others(true)}
            <MenuButton />
          </div>
        </div>
        <Title view={view} country={country} />
        <div className={s.stack}>
          {card}
          <Table rows={view.table} country={view.series.country} />
          {notes}
          <Fixtures fixtures={view.fixtures} mySlot={mySlot} day={day} country={view.series.country} />
        </div>
      </div>
    </div>
  );
}

/** Loading, a failure, or a guest's invitation, in the league's frame. */
function Placeholder({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const leagueBack = useApp((st) => st.leagueBack);
  const layout = useLayoutMode();
  return (
    <div className={['cg-ui', ui.screen].join(' ')}>
      {layout === 'desktop' && <DesktopHeader />}
      <div className={s.phoneBg} aria-hidden="true">
        <ArenaBackdrop tone="menu" shade="linear-gradient(180deg,rgba(20,19,18,.8),rgba(20,19,18,.3) 45%,#141312)" />
      </div>
      <div className={ui.column}>
        <div className={s.topBar}>
          <button type="button" className={ui.iconBtn} onClick={leagueBack} aria-label={t('common.back')}>
            <span aria-hidden="true">←</span>
          </button>
          {layout !== 'desktop' && <MenuButton />}
        </div>
        <div className={s.placeholder}>{children}</div>
      </div>
    </div>
  );
}

function Invite({ signup }: { signup: boolean }) {
  const { t } = useTranslation();
  const openAuth = useApp((st) => st.openAuth);
  return (
    <Placeholder>
      <h1 className={s.placeholderTitle}>{t(signup ? 'screens.league.signupTitle' : 'screens.league.guestTitle')}</h1>
      <p className={s.placeholderBody}>{t(signup ? 'screens.league.signupBody' : 'screens.league.guestBody')}</p>
      <button type="button" className={[ui.primary, s.placeholderBtn].join(' ')} onClick={() => openAuth(signup ? 'signup' : 'register')}>
        {t(signup ? 'screens.league.completeSignup' : 'screens.league.register')}
        <span className={ui.arrow} aria-hidden="true">
          →
        </span>
      </button>
    </Placeholder>
  );
}

function Loading({ failed, retry }: { failed: boolean; retry: () => void }) {
  const { t } = useTranslation();
  return (
    <Placeholder>
      {failed ? (
        <>
          <p className={s.placeholderBody}>{t('screens.league.failed')}</p>
          <button type="button" className={[ui.secondary, s.placeholderBtn].join(' ')} onClick={retry}>
            {t('screens.league.retry')}
          </button>
        </>
      ) : (
        <p className={s.placeholderBody} role="status">
          {t('screens.league.loading')}
        </p>
      )}
    </Placeholder>
  );
}

/** Moved here when a season ended, and this country's season isn't over yet (L8). */
function Moving({ country }: { country: string }) {
  const { t, i18n } = useTranslation();
  const openLeague = useApp((st) => st.openLeague);
  return (
    <Placeholder>
      <Flag country={country} className={s.movingFlag} />
      <h1 className={s.placeholderTitle}>{t('screens.league.movingTitle', { country: countryName(country, i18n.language) })}</h1>
      <p className={s.placeholderBody}>{t('screens.league.movingBody')}</p>
      <button type="button" className={[ui.secondary, s.placeholderBtn].join(' ')} onClick={() => openLeague('leagues')}>
        {t('screens.league.otherLeagues')}
      </button>
    </Placeholder>
  );
}

function MyLeague() {
  const mine = useLeague((st) => st.mine);
  const failed = useLeague((st) => st.failed);
  const load = useLeague((st) => st.load);
  const account = useApp((st) => st.account);
  // Again once they've registered or finished signup in the sheet, and every so often while
  // the screen is open (results, the live window, an opponent waiting).
  useEffect(() => {
    void load(true);
    const id = setInterval(() => void load(true), 20_000);
    return () => clearInterval(id);
  }, [load, account]);
  if (mine?.status === 'guest') return <Invite signup={false} />;
  if (mine?.status === 'signup') return <Invite signup />;
  if (mine?.status === 'moving') return <Moving country={mine.country} />;
  if (mine?.status !== 'ok') return <Loading failed={failed} retry={() => void load(true)} />;
  return (
    <Page view={mine} country={mine.country} mySlot={mine.me.slot} joinedDay={mine.me.joinedDay} waiting={mine.me.opponentWaiting} movingTo={mine.movingTo} />
  );
}

function OtherSeries({ id }: { id: string }) {
  const [view, setView] = useState<(SeriesView & { country: string }) | null>(null);
  const [failed, setFailed] = useState(false);
  const mine = useLeague((st) => st.mine);
  const load = () => {
    setFailed(false);
    import('../../net/leagueApi')
      .then((api) => api.fetchSeries(id))
      .then(setView, () => setFailed(true));
  };
  useEffect(load, [id]);
  if (!view) return <Loading failed={failed} retry={load} />;
  // Browsing their own series from the pyramid: it's still theirs.
  const own = mine?.status === 'ok' && mine.series.id === id;
  return <Page view={view} country={view.country} mySlot={own ? mine.me.slot : null} joinedDay={own ? mine.me.joinedDay : null} />;
}

export function LeagueScreen() {
  const target = useApp((st) => st.leagueTarget);
  useScrollTop();
  return target?.series ? <OtherSeries id={target.series} /> : <MyLeague />;
}
